// ── AuthContext.jsx ────────────────────────────────────────────────────────────
// يدير حالة المصادقة (Firebase Auth) على مستوى التطبيق:
//   - تسجيل الدخول كزائر
//   - تسجيل الدخول وإنشاء حساب بالبريد وكلمة المرور
//   - التحقق من البريد الإلكتروني
//   - حفظ اسم المستخدم في Firebase و localStorage
//   - نظام ID احترافي لكل مستخدم (زائر أو عضو)
//   - نظام ملف شخصي متكامل (اسم، نبذة، تاريخ ميلاد، صورة، جنس)
//   - نظام @username مميز للمسجلين (مثل تويتر/إنستغرام)
//   - تسجيل الخروج

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import {
  onAuthStateChanged,
  signInAnonymously,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  sendEmailVerification,
  reload,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
} from "firebase/auth";
import { ref, set, get, onValue, off } from "firebase/database";
import { auth, db, googleProvider } from "../firebase";

// ── الثوابت ──────────────────────────────────────────────────────────────────
const LS_USERNAME_KEY   = "user_display_name";
const LS_AUTH_DONE_KEY  = "auth_completed";
const LS_AVATAR_COLOR   = "user_avatar_color";
const LS_USER_ID_KEY    = "leaksmercy_uid";   // الـ ID المحلي للزائر
const LS_GENDER_KEY     = "user_gender";    // جنس المستخدم (للزوار أيضاً)

// ── ثوابت @username ──────────────────────────────────────────────────────────
const USERNAME_COOLDOWN_MS = 6 * 30 * 24 * 60 * 60 * 1000; // 6 أشهر بالمللي ثانية
const USERNAME_MIN_LENGTH  = 3;
const USERNAME_MAX_LENGTH  = 20;
// الأحرف المسموح بها: حروف إنجليزية، أرقام، شرطة سفلية، نقطة
const USERNAME_REGEX       = /^[a-zA-Z0-9_.]+$/;
// لا يبدأ أو ينتهي بنقطة أو شرطة سفلية
const USERNAME_EDGE_REGEX  = /^[._]|[._]$/;
// لا توجد نقطتان أو شرطتان متتاليتان
const USERNAME_CONSECUTIVE_REGEX = /[_.]{2,}/;

// ── توليد ID احترافي فريد ─────────────────────────────────────────────────────
const generateUserId = () => {
  const ts   = Date.now().toString(36).toUpperCase().slice(-6);
  const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
  const uid2 = Math.random().toString(36).substring(2, 5).toUpperCase();
  return `OL-${ts}${rand}-${uid2}`;
};

// الحصول على أو إنشاء ID محلي (للزوار)
const getOrCreateLocalId = () => {
  try {
    const stored = localStorage.getItem(LS_USER_ID_KEY);
    if (stored && stored.startsWith("OL-")) return stored;
    const newId = generateUserId();
    localStorage.setItem(LS_USER_ID_KEY, newId);
    return newId;
  } catch {
    return generateUserId();
  }
};

// ── السياق (Context) ─────────────────────────────────────────────────────────
export const AuthContext = createContext({
  user:                null,
  username:            "",
  userId:              "",
  isGuest:             false,
  authLoading:         true,
  authCompleted:       false,
  profile:             null,
  profileLoading:      false,
  setAuthCompleted:    () => {},
  loginWithEmail:      async () => {},
  signupWithEmail:     async () => {},
  sendVerificationEmail: async () => {},
  reloadUser:          async () => {},
  loginAsGuest:        async () => {},
  loginWithGoogle:     async () => {},   // Google Sign-In / Sign-Up
  logout:              async () => {},
  updateUsername:      async () => {},
  saveProfile:         async () => {},
  uploadProfileImage:  async () => {},
  // === جديد ===
  saveGender:          async () => {},   // حفظ الجنس (للجميع)
  checkUsernameAvailable: async () => {}, // التحقق من توفر @username
  saveUniqueUsername:  async () => {},   // حفظ @username المميز
});

// ── المزود (Provider) ────────────────────────────────────────────────────────
export const AuthProvider = ({ children }) => {
  const [user,           setUser]           = useState(null);
  const [username,       setUsername]       = useState("");
  const [userId,         setUserId]         = useState("");
  const [authLoading,    setAuthLoading]    = useState(true);
  const [profile,        setProfile]        = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const [authCompleted, setAuthCompletedState] = useState(() => {
    try { return localStorage.getItem(LS_AUTH_DONE_KEY) === "true"; } catch { return false; }
  });

  const setAuthCompleted = useCallback((val) => {
    try { localStorage.setItem(LS_AUTH_DONE_KEY, String(val)); } catch {}
    setAuthCompletedState(val);
  }, []);

  // ── تحميل الملف الشخصي من Firebase ────────────────────────────────────────
  const loadProfile = useCallback(async (uid) => {
    if (!uid) return;
    setProfileLoading(true);
    try {
      const profileRef = ref(db, `profiles/${uid}`);
      const snap = await get(profileRef);
      if (snap.exists()) {
        setProfile(snap.val());
      } else {
        setProfile(null);
      }
    } catch {
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  // ── الاستماع لتغيرات حالة المصادقة ─────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {

      // نتحقق من البريد الإلكتروني فقط لمستخدمي Email/Password
      // مستخدمو Google لا يحتاجون تحقق إضافي (Google يضمن صحة الحساب)
      const isGoogleUser = firebaseUser?.providerData?.some(
        (p) => p.providerId === "google.com"
      );

      if (
        firebaseUser &&
        !firebaseUser.isAnonymous &&
        !isGoogleUser &&
        !firebaseUser.emailVerified
      ) {
        setAuthLoading(false);
        signOut(auth);
        return;
      }

      setUser(firebaseUser);

      if (firebaseUser) {
        let resolvedId;
        if (!firebaseUser.isAnonymous) {
          resolvedId = firebaseUser.uid;
        } else {
          resolvedId = getOrCreateLocalId();
        }
        setUserId(resolvedId);

        const fbName = firebaseUser.displayName || "";
        const lsName = (() => { try { return localStorage.getItem(LS_USERNAME_KEY) || ""; } catch { return ""; } })();
        const name   = fbName || lsName || "";
        setUsername(name);

        if (fbName) {
          try { localStorage.setItem(LS_AUTH_DONE_KEY, "true"); } catch {}
          setAuthCompletedState(true);
        }

        if (!firebaseUser.isAnonymous) {
          await loadProfile(resolvedId);
        }
      } else {
        setUsername("");
        setUserId("");
        setProfile(null);
        setAuthCompletedState(false);
        try { localStorage.removeItem(LS_AUTH_DONE_KEY); } catch {}
        try { localStorage.removeItem(LS_USERNAME_KEY); } catch {}
        // لا نمسح الجنس من localStorage لأنه يجب أن يبقى للزائرين
      }

      setAuthLoading(false);
    });
    return () => unsub();
  }, [loadProfile]);

  const isGuest = user?.isAnonymous === true;

  // ── تسجيل الدخول بالبريد الإلكتروني ────────────────────────────────────────
  const loginWithEmail = useCallback(async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    if (!result.user.emailVerified) {
      await signOut(auth);
      const err = new Error("email-not-verified");
      err.code = "auth/email-not-verified";
      throw err;
    }
    return result;
  }, []);

  // ── إنشاء حساب بالبريد الإلكتروني ──────────────────────────────────────────
  const signupWithEmail = useCallback(async (email, password) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    return result;
  }, []);

  // ── إرسال رسالة تفعيل البريد ───────────────────────────────────────────────
  const sendVerificationEmail = useCallback(async () => {
    if (auth.currentUser) {
      await sendEmailVerification(auth.currentUser);
    }
  }, []);

  // ── إعادة تحميل بيانات المستخدم ────────────────────────────────────────────
  const reloadUser = useCallback(async () => {
    if (auth.currentUser) {
      await reload(auth.currentUser);
      setUser({ ...auth.currentUser });
      return auth.currentUser;
    }
    return null;
  }, []);

  // ── تسجيل الدخول كزائر ─────────────────────────────────────────────────────
  const loginAsGuest = useCallback(async () => {
    const result = await signInAnonymously(auth);
    return result;
  }, []);

  // ── تسجيل الدخول / إنشاء حساب عبر Google ──────────────────────────────────
  // يستخدم Popup في سطح المكتب ويتراجع إلى Redirect في الموبايل تلقائياً.
  // بعد نجاح المصادقة، يحفظ بيانات المستخدم الأساسية في Firebase Realtime DB
  // ويُرجع { user, isNewUser } حتى يقرر الـ caller التوجيه المناسب.
  const loginWithGoogle = useCallback(async () => {
    // الأجهزة التي لا تدعم الـ popup (webview / iOS Safari بعض الإصدارات)
    // نستخدم popup أولاً وإذا فشل نتراجع لـ redirect
    let result;
    try {
      result = await signInWithPopup(auth, googleProvider);
    } catch (popupErr) {
      // أخطاء تعني أن الـ popup محجوب — نتراجع لـ redirect
      if (
        popupErr.code === "auth/popup-blocked" ||
        popupErr.code === "auth/popup-closed-by-user" ||
        popupErr.code === "auth/cancelled-popup-request"
      ) {
        // في حالة الـ redirect، العملية تتم في صفحة جديدة
        // سيُعالج النتيجة useEffect خاص عند إعادة التحميل
        await signInWithRedirect(auth, googleProvider);
        return { redirected: true };
      }
      throw popupErr;
    }

    const firebaseUser = result.user;

    // تحديد هل هذا مستخدم جديد أم موجود مسبقاً
    const isNewUser =
      result._tokenResponse?.isNewUser === true ||
      (firebaseUser.metadata.creationTime === firebaseUser.metadata.lastSignInTime);

    // حفظ / تحديث ملف المستخدم في Firebase Realtime DB
    const uid        = firebaseUser.uid;
    const profileRef = ref(db, `profiles/${uid}`);
    try {
      const snap     = await get(profileRef);
      const existing = snap.exists() ? snap.val() : {};

      // نحدّث فقط الحقول الأساسية وندع بقية البيانات كما هي
      const updated = {
        ...existing,
        uid,
        displayName: existing.displayName || firebaseUser.displayName || "",
        avatarUrl:   existing.avatarUrl   || firebaseUser.photoURL    || "",
        email:       firebaseUser.email   || "",
        provider:    "google",
        isAnonymous: false,
        updatedAt:   Date.now(),
        createdAt:   existing.createdAt || Date.now(),
      };
      await set(profileRef, updated);
      setProfile(updated);
    } catch {
      // نستمر حتى لو فشل حفظ الملف الشخصي
    }

    return { user: firebaseUser, isNewUser };
  }, []);

  // ── معالجة نتيجة Google Redirect (عند العودة من صفحة Google) ─────────────
  // يعمل مرة واحدة عند تحميل التطبيق لمعالجة نتيجة signInWithRedirect
  useEffect(() => {
    getRedirectResult(auth)
      .then(async (result) => {
        if (!result) return; // لا توجد نتيجة redirect معلّقة
        const firebaseUser = result.user;
        if (!firebaseUser) return;

        const uid        = firebaseUser.uid;
        const profileRef = ref(db, `profiles/${uid}`);
        try {
          const snap     = await get(profileRef);
          const existing = snap.exists() ? snap.val() : {};
          const updated  = {
            ...existing,
            uid,
            displayName: existing.displayName || firebaseUser.displayName || "",
            avatarUrl:   existing.avatarUrl   || firebaseUser.photoURL    || "",
            email:       firebaseUser.email   || "",
            provider:    "google",
            isAnonymous: false,
            updatedAt:   Date.now(),
            createdAt:   existing.createdAt || Date.now(),
          };
          await set(profileRef, updated);
          setProfile(updated);
        } catch {}
      })
      .catch(() => {
        // نتجاهل أخطاء getRedirectResult لأنها قد تظهر عند عدم وجود redirect
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── تسجيل الخروج ───────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    try { localStorage.removeItem(LS_AUTH_DONE_KEY); } catch {}
    try { localStorage.removeItem(LS_USERNAME_KEY); } catch {}
    // الجنس يبقى محفوظاً في localStorage
    await signOut(auth);
  }, []);

  // ── تحديث اسم المستخدم (يعمل للأعضاء والزوار) ──────────────────────────────
  const updateUsername = useCallback(async (name) => {
    if (!name || !auth.currentUser) return;
    const trimmed = name.trim();
    if (!auth.currentUser.isAnonymous) {
      await updateProfile(auth.currentUser, { displayName: trimmed });
    }
    try { localStorage.setItem(LS_USERNAME_KEY, trimmed); } catch {}
    setUsername(trimmed);
  }, []);

  // ── حفظ الجنس (للجميع: زوار + مسجلين) ──────────────────────────────────────
  // الجنس يُحفظ في localStorage دائماً (للزوار والمسجلين)
  // وللجميع (زوار ومسجلين) يُحفظ أيضاً في Firebase profiles/{uid}
  const saveGender = useCallback(async (gender) => {
    // حفظ في localStorage (للجميع)
    try { localStorage.setItem(LS_GENDER_KEY, gender); } catch {}

    // إذا كان هناك مستخدم (زائر أو مسجّل)، نحفظ في Firebase
    if (auth.currentUser) {
      const uid = auth.currentUser.uid;
      const profileRef = ref(db, `profiles/${uid}`);

      try {
        const snap = await get(profileRef);
        const existing = snap.exists() ? snap.val() : {};
        const updated = {
          ...existing,
          uid,
          gender,
          isAnonymous: auth.currentUser.isAnonymous,
          updatedAt: Date.now(),
          createdAt: existing.createdAt || Date.now(),
        };
        await set(profileRef, updated);
        setProfile(updated);
      } catch (err) {
        // في حالة فشل Firebase، نحدث state محلياً فقط
        setProfile(prev => prev ? { ...prev, gender } : { gender });
      }
    } else {
      // بدون مستخدم: نحدث state محلياً فقط
      setProfile(prev => prev ? { ...prev, gender } : { gender });
    }
  }, []);

  // ── حفظ الملف الشخصي الكامل في Firebase ───────────────────────────────────
  const saveProfile = useCallback(async (profileData) => {
    if (!auth.currentUser || auth.currentUser.isAnonymous) {
      throw new Error("guests-cannot-have-profile");
    }
    const uid = auth.currentUser.uid;
    const profileRef = ref(db, `profiles/${uid}`);

    // نجلب الجنس من localStorage إذا لم يُمرَّر
    const genderFromLS = (() => { try { return localStorage.getItem(LS_GENDER_KEY) || ""; } catch { return ""; } })();

    const dataToSave = {
      uid,
      displayName:     profileData.displayName     || "",
      bio:             profileData.bio              || "",
      birthdate:       profileData.birthdate        || "",
      avatarUrl:       profileData.avatarUrl        || "",
      avatarColor:     profileData.avatarColor      || "#e50000",
      gender:          profileData.gender           !== undefined
                         ? profileData.gender
                         : (profile?.gender || genderFromLS || ""),
      // @username — نحافظ على القيمة الموجودة ولا نمسحها
      uniqueUsername:      profile?.uniqueUsername      || "",
      usernameSetAt:       profile?.usernameSetAt       || null,
      updatedAt:       Date.now(),
      createdAt:       profile?.createdAt || Date.now(),
    };

    await set(profileRef, dataToSave);

    if (profileData.displayName) {
      await updateProfile(auth.currentUser, { displayName: profileData.displayName.trim() });
      try { localStorage.setItem(LS_USERNAME_KEY, profileData.displayName.trim()); } catch {}
      setUsername(profileData.displayName.trim());
    }

    // حفظ الجنس في localStorage أيضاً
    if (dataToSave.gender) {
      try { localStorage.setItem(LS_GENDER_KEY, dataToSave.gender); } catch {}
    }

    setProfile(dataToSave);
    return dataToSave;
  }, [profile]);

  // ── التحقق من توفر @username ─────────────────────────────────────────────
  // يتحقق من:
  // 1. صيغة اليوزر نيم (أحرف، أرقام، نقطة، شرطة سفلية)
  // 2. عدم تكراره في Firebase
  // يُرجع: { available: bool, error: string | null }
  const checkUsernameAvailable = useCallback(async (rawUsername) => {
    const uname = rawUsername.toLowerCase().trim();

    // فحص الصيغة
    if (uname.length < USERNAME_MIN_LENGTH) {
      return { available: false, error: "too_short" };
    }
    if (uname.length > USERNAME_MAX_LENGTH) {
      return { available: false, error: "too_long" };
    }
    if (!USERNAME_REGEX.test(uname)) {
      return { available: false, error: "invalid_chars" };
    }
    if (USERNAME_EDGE_REGEX.test(uname)) {
      return { available: false, error: "invalid_edge" };
    }
    if (USERNAME_CONSECUTIVE_REGEX.test(uname)) {
      return { available: false, error: "consecutive_special" };
    }

    // فحص الكلمات المحجوزة
    const reserved = ["admin","leaksmercy","support","owner","staff","mod","moderator","help","official","system","root"];
    if (reserved.includes(uname)) {
      return { available: false, error: "reserved" };
    }

    // فحص التوفر في Firebase
    try {
      const usernamesRef = ref(db, `usernames/${uname}`);
      const snap = await get(usernamesRef);
      if (snap.exists()) {
        // إذا كان هذا اليوزر يخص المستخدم الحالي (نفس الـ uid)
        const ownerUid = snap.val();
        if (auth.currentUser && ownerUid === auth.currentUser.uid) {
          return { available: true, error: null, isSelf: true };
        }
        return { available: false, error: "taken" };
      }
      return { available: true, error: null };
    } catch {
      return { available: false, error: "network_error" };
    }
  }, []);

  // ── حفظ @username المميز ────────────────────────────────────────────────────
  // - يتحقق من انقضاء فترة الـ 6 أشهر
  // - يسجل اليوزر نيم في مسار /usernames/{username} = uid
  // - يحدث ملف المستخدم
  const saveUniqueUsername = useCallback(async (rawUsername) => {
    if (!auth.currentUser || auth.currentUser.isAnonymous) {
      throw new Error("guests-cannot-set-username");
    }

    const uname = rawUsername.toLowerCase().trim();
    const uid   = auth.currentUser.uid;

    // فحص فترة التبريد (6 أشهر) — إذا كان لديه يوزر نيم بالفعل
    if (profile?.usernameSetAt && profile?.uniqueUsername) {
      const elapsed = Date.now() - profile.usernameSetAt;
      if (elapsed < USERNAME_COOLDOWN_MS) {
        const remainMs   = USERNAME_COOLDOWN_MS - elapsed;
        const remainDays = Math.ceil(remainMs / (24 * 60 * 60 * 1000));
        const err = new Error("cooldown");
        err.remainDays = remainDays;
        throw err;
      }
    }

    // تحقق من التوفر
    const check = await checkUsernameAvailable(uname);
    if (!check.available && !check.isSelf) {
      const err = new Error(check.error || "unavailable");
      throw err;
    }

    // إذا كان لديه يوزر نيم قديم، نحذفه من /usernames
    if (profile?.uniqueUsername && profile.uniqueUsername !== uname) {
      try {
        const oldRef = ref(db, `usernames/${profile.uniqueUsername}`);
        await set(oldRef, null);
      } catch {}
    }

    // تسجيل اليوزر نيم الجديد في /usernames/{uname} = uid
    const usernameRef = ref(db, `usernames/${uname}`);
    await set(usernameRef, uid);

    // تحديث ملف المستخدم
    const profileRef = ref(db, `profiles/${uid}`);
    const snap = await get(profileRef);
    const existing = snap.exists() ? snap.val() : {};
    const updated = {
      ...existing,
      uid,
      uniqueUsername: uname,
      usernameSetAt:  Date.now(),
      updatedAt:      Date.now(),
      createdAt:      existing.createdAt || Date.now(),
    };
    await set(profileRef, updated);
    setProfile(updated);

    return uname;
  }, [profile, checkUsernameAvailable]);

  // ── رفع صورة الملف الشخصي عبر imgbb (مع ضغط + WebP) ─────────────────────
  const uploadProfileImage = useCallback(async (file) => {
    const IMGBB_API_KEY = "2cec769c472e139124ffbf8154af63a1";

    // ① ضغط + تحويل WebP قبل الرفع
    let processedFile = file;
    try {
      const { compressProfileImage } = await import("../utils/imageCompressor");
      processedFile = await compressProfileImage(file);
    } catch { /* fallback: استخدم الملف الأصلي */ }

    // ② تحويل لـ base64 (أكثر موثوقية مع imgbb)
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = (e) => resolve(e.target.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(processedFile);
    });

    const formData = new FormData();
    formData.append("image", base64);
    formData.append("key", IMGBB_API_KEY);

    const response = await fetch("https://api.imgbb.com/1/upload", {
      method: "POST",
      body: formData,
    });

    if (!response.ok) throw new Error("image-upload-failed");

    const data = await response.json();
    if (!data.success) throw new Error(data.error?.message || "image-upload-failed");

    return data.data.url;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        username,
        userId,
        isGuest,
        authLoading,
        authCompleted,
        setAuthCompleted,
        profile,
        profileLoading,
        loginWithEmail,
        signupWithEmail,
        sendVerificationEmail,
        reloadUser,
        loginAsGuest,
        loginWithGoogle,
        logout,
        updateUsername,
        saveProfile,
        uploadProfileImage,
        loadProfile,
        // === جديد ===
        saveGender,
        checkUsernameAvailable,
        saveUniqueUsername,
        // ثوابت مفيدة للكومبوننتس
        USERNAME_MIN_LENGTH,
        USERNAME_MAX_LENGTH,
        USERNAME_COOLDOWN_MS,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
